// Downloadable Windows setup file (roadmap #100 follow-up, UX rework): builds, entirely
// client-side, a self-contained .cmd file that configures a staff workstation so the
// installed window keeps receiving call alerts when closed and opens automatically on
// sign-in. No server file and no new route - the file text is generated in the browser
// and handed to the user as a Blob download.
//
// The file is a batch/PowerShell polyglot (the well-known "<# : ... #>" header): a plain
// double click runs it via cmd.exe, which re-invokes PowerShell against the same file with
// its arguments forwarded, without the staff member ever seeing or typing a PowerShell
// command. This replaces the previous .ps1-only download (too technical to run by double
// click) per operator feedback.
//
// The script itself may name Microsoft Edge / Google Chrome and their own registry policy
// paths (the user's own software, sanctioned per the installable-window fallback), but must
// never reveal our internal stack.

export type SetupBrowser = 'edge' | 'chrome'

// Preselect the browser param from the current user agent so the downloaded file defaults
// to whatever the staff member is actually running; still overridable via -Browser when
// running the file manually from a command line.
export const detectBrowser = (): SetupBrowser => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  return /Edg\//.test(ua) ? 'edge' : 'chrome'
}

// Resolves the app URL the same way the rest of the app reads its own base:
// origin + Vite's per-environment BASE_URL ('/' in dev/staging, '/caller-beta/'
// in production - see vite.config.ts and staff/sso.ts for the same pattern).
export const currentAppUrl = (): string =>
  `${window.location.origin}${import.meta.env.BASE_URL}`

export const buildSetupScript = (appUrl: string, browser: SetupBrowser): string => `<# : batch entry
@echo off
powershell -NoProfile -ExecutionPolicy Bypass -Command "$sc=[IO.File]::ReadAllText('%~f0'); & ([scriptblock]::Create($sc)) %*"
exit /b %errorlevel%
: end batch #>

# Configures a Windows workstation so the QR Phone installed window keeps
# ringing with the window closed and starts on login (roadmap #100 follow-up).
#
# What it does (per current user, no admin needed):
#   1. Browser background mode ON via browser policy (registry, HKCU).
#   2. Startup shortcut that opens the app as its own window on login.
#
# Usage: double-click this file. To pass options, run it from a command
# prompt instead:
#   qr-phone-setup.cmd
#   qr-phone-setup.cmd -Browser chrome
#   qr-phone-setup.cmd -AppUrl https://qr-phone.com/caller-beta/
#   qr-phone-setup.cmd -Uninstall               # undo both changes
#   qr-phone-setup.cmd -DryRun                  # show planned actions, change nothing

param(
    [ValidateSet('edge', 'chrome')] [string]$Browser = '${browser}',
    [string]$AppUrl = '${appUrl}',
    [switch]$Uninstall,
    [switch]$DryRun
)

$ErrorActionPreference = 'Stop'

# The shortcut opens the staff surface directly, carrying a verification marker in
# the query string: the web application persists it on launch and the setup panel
# can then show the auto-start as verified. The app routes via the URL fragment,
# so the marker goes in the query (before the '#') and the staff route after it.
if (-not $AppUrl.EndsWith('/')) { $AppUrl = "$AppUrl/" }
$startUrl = $AppUrl + '?autostart=1#/staff'

if ($Browser -eq 'edge') {
    $policyKey = 'HKCU:\\SOFTWARE\\Policies\\Microsoft\\Edge'
    $exe = Join-Path \${env:ProgramFiles(x86)} 'Microsoft\\Edge\\Application\\msedge.exe'
    if (-not (Test-Path $exe)) { $exe = Join-Path $env:ProgramFiles 'Microsoft\\Edge\\Application\\msedge.exe' }
} else {
    $policyKey = 'HKCU:\\SOFTWARE\\Policies\\Google\\Chrome'
    $exe = Join-Path $env:ProgramFiles 'Google\\Chrome\\Application\\chrome.exe'
    if (-not (Test-Path $exe)) { $exe = Join-Path \${env:ProgramFiles(x86)} 'Google\\Chrome\\Application\\chrome.exe' }
}
$exeFound = Test-Path $exe

$shortcut = Join-Path ([Environment]::GetFolderPath('Startup')) 'QR Phone.lnk'

if ($Uninstall) {
    if ($DryRun) {
        Write-Host '[DryRun] Nothing will be changed. Planned actions:'
        Write-Host "  - Remove policy value: $policyKey\\BackgroundModeEnabled (if present)"
        Write-Host "  - Remove startup shortcut: $shortcut (if present)"
        exit 0
    }
    if (Test-Path $policyKey) {
        Remove-ItemProperty -Path $policyKey -Name 'BackgroundModeEnabled' -ErrorAction SilentlyContinue
    }
    Remove-Item -Path $shortcut -ErrorAction SilentlyContinue
    Write-Host 'Removed background-mode policy and startup shortcut.'
    exit 0
}

if ($DryRun) {
    Write-Host '[DryRun] Nothing will be changed. Planned actions:'
    Write-Host "  - Set policy DWord: $policyKey\\BackgroundModeEnabled = 1"
    if ($exeFound) {
        Write-Host "  - Create startup shortcut: $shortcut -> $exe --app=$startUrl"
    } else {
        Write-Host "  - Create startup shortcut: $shortcut -> <$Browser executable not found> --app=$startUrl"
        Write-Host "  - NOTE: $Browser does not appear to be installed at the expected path; install it before running without -DryRun."
    }
    exit 0
}

if (-not $exeFound) { throw "Browser executable not found for '$Browser'. Install it first." }

# 1. Background mode: keep the push service alive when every window is closed.
New-Item -Path $policyKey -Force | Out-Null
Set-ItemProperty -Path $policyKey -Name 'BackgroundModeEnabled' -Type DWord -Value 1

# 2. Startup shortcut: open the app in its own window on login.
$ws = New-Object -ComObject WScript.Shell
$lnk = $ws.CreateShortcut($shortcut)
$lnk.TargetPath = $exe
$lnk.Arguments = "--app=$startUrl"
$lnk.Description = 'QR Phone'
$lnk.Save()

Write-Host "Done. Background mode enabled ($Browser) and startup shortcut created:"
Write-Host "  $shortcut -> $exe --app=$startUrl"
Write-Host 'Sign out and back in (or reboot) to verify the window opens on login.'
`

export const SETUP_SCRIPT_FILENAME = 'qr-phone-setup.cmd'

// Builds the setup file for the currently running app/browser and triggers a browser
// download via a temporary anchor - no server round-trip.
export const downloadSetupScript = (): void => {
  const script = buildSetupScript(currentAppUrl(), detectBrowser())
  const blob = new Blob([script], { type: 'application/octet-stream' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = SETUP_SCRIPT_FILENAME
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
